import React, { useState, useCallback, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  Alert,
} from "reactstrap";
import { toast } from "react-toastify";
import {
  mercantilBuscarYRegistrar,
  getsolicitudes,
} from "../../helpers/backend_helper";

// Loader inline para botones (3 puntos animados)
const LoaderButton = () => (
  <svg
    className="me-2"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <style>{`
      .mbr_dot { animation: mbr_pulse 1.2s cubic-bezier(0.52,0.6,0.25,0.99) infinite }
      .mbr_dot2 { animation-delay: 0.2s }
      .mbr_dot3 { animation-delay: 0.4s }
      @keyframes mbr_pulse {
        0% { transform: translateY(0) scale(1); opacity: 1 }
        50% { transform: translateY(8px) scale(0.6); opacity: 0.4 }
        100% { transform: translateY(0) scale(1); opacity: 1 }
      }
    `}</style>
    <circle className="mbr_dot" cx="4" cy="12" r="3" fill="currentColor" />
    <circle className="mbr_dot mbr_dot2" cx="12" cy="12" r="3" fill="currentColor" />
    <circle className="mbr_dot mbr_dot3" cx="20" cy="12" r="3" fill="currentColor" />
  </svg>
);

// Mapea errores HTTP a mensajes amigables
const mensajeErrorAmigable = (error) => {
  const status = error?.status || error?.response?.status;
  const detail = error?.response?.data?.detail || error?.detail;

  if (detail && typeof detail === "string") return detail;

  switch (status) {
    case 400:
      return "La solicitud no es válida. Verifique el identificador ingresado.";
    case 403:
      return "No tiene permisos para realizar esta operación.";
    case 404:
      return "La orden no existe o no está disponible en Mercantil.";
    case 409:
      return "La orden ya existe en el sistema.";
    case 422:
      return "Los datos de la orden no cumplen con los requisitos.";
    case 500:
      return "Ocurrió un error interno en el servidor. Intente nuevamente más tarde.";
    case 502:
    case 503:
      return "El portal de Mercantil no está disponible en este momento. Intente más tarde.";
    case 504:
      return "La consulta a Mercantil tardó demasiado. Intente nuevamente.";
    default:
      return "No se pudo completar la operación. Por favor intente de nuevo.";
  }
};

// Indicador de pasos (Consultando → Validando → Registrando)
const PASOS = [
  { key: "consultando", label: "Consultando Mercantil" },
  { key: "validando", label: "Validando datos" },
  { key: "registrando", label: "Registrando en Farme" },
];

const indicePaso = (paso) => PASOS.findIndex((p) => p.key === paso);

const StepsIndicator = ({ paso, error }) => {
  const idx = indicePaso(paso);
  return (
    <div className="d-flex align-items-center justify-content-between gap-2 my-3 p-3 bg-light rounded">
      {PASOS.map((p, i) => {
        const completo = paso === "done" ? !error : i < idx;
        const enCurso = i === idx && paso !== "done";
        const fallido = error && i === idx;
        let icon = "ri-checkbox-blank-circle-line text-muted";
        if (fallido) icon = "ri-close-circle-fill text-danger";
        else if (completo) icon = "ri-checkbox-circle-fill text-success";
        else if (enCurso) icon = "ri-loader-4-line text-primary";

        return (
          <React.Fragment key={p.key}>
            <div className="d-flex flex-column align-items-center text-center" style={{ flex: 1 }}>
              <i
                className={`${icon} fs-22 ${enCurso ? "spin" : ""}`}
                style={enCurso ? { animation: "mbrSpin 1s linear infinite" } : {}}
              ></i>
              <small
                className={`mt-1 ${
                  fallido
                    ? "text-danger fw-semibold"
                    : completo
                    ? "text-success fw-semibold"
                    : enCurso
                    ? "text-primary fw-semibold"
                    : "text-muted"
                }`}
              >
                {p.label}
              </small>
            </div>
            {i < PASOS.length - 1 && (
              <div
                className={`flex-grow-0 ${i < idx || (paso === "done" && !error) ? "bg-success" : "bg-secondary-subtle"}`}
                style={{ height: 2, width: 30 }}
              />
            )}
          </React.Fragment>
        );
      })}
      <style>{`@keyframes mbrSpin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
    </div>
  );
};

const MercantilBuscarRegistrarModal = ({ isOpen, toggle, onSuccess }) => {
  const [identificador, setIdentificador] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [paso, setPaso] = useState("idle"); // idle | consultando | validando | registrando | done
  const [resultado, setResultado] = useState(null);
  const [solicitudIdEncontrada, setSolicitudIdEncontrada] = useState(null);
  const [buscandoEnlace, setBuscandoEnlace] = useState(false);

  // Reset al cerrar
  useEffect(() => {
    if (!isOpen) {
      setIdentificador("");
      setBuscando(false);
      setPaso("idle");
      setResultado(null);
      setSolicitudIdEncontrada(null);
      setBuscandoEnlace(false);
    }
  }, [isOpen]);

  // Buscar el id de la solicitud para construir enlace al detalle
  const buscarSolicitudId = useCallback(async (ident) => {
    try {
      setBuscandoEnlace(true);
      // Intentar varias veces porque el listado puede tardar un instante en
      // reflejar la solicitud recién creada (índices, replicas, etc.).
      const intentar = async () => {
        const query = `query=${encodeURIComponent(ident)}&limit=5&offset=0`;
        const res = await getsolicitudes(query);
        // Backend retorna: { data: [lista, total, stats], ... }
        const payload = res?.data?.data;
        const lista = Array.isArray(payload) ? payload[0] : [];
        return (lista || []).find(
          (s) =>
            (s?.identificador || "").toString().toUpperCase().trim() ===
            ident.toUpperCase().trim()
        );
      };

      let match = await intentar();
      // Reintentar hasta 3 veces con pequeño backoff si no se encontró
      for (let i = 0; i < 3 && !match; i++) {
        await new Promise((r) => setTimeout(r, 700));
        match = await intentar();
      }

      const sid = match?.id || match?._id;
      if (sid) {
        setSolicitudIdEncontrada(sid);
      } else {
        console.warn(`No se encontró id para identificador ${ident} tras reintentos.`);
      }
    } catch (err) {
      console.warn("No se pudo obtener id de la solicitud:", err);
    } finally {
      setBuscandoEnlace(false);
    }
  }, []);

  const handleBuscar = async () => {
    const ident = identificador.trim().toUpperCase();
    if (!ident) {
      toast.warning("Ingrese un identificador de orden");
      return;
    }

    setBuscando(true);
    setResultado(null);
    setSolicitudIdEncontrada(null);
    setPaso("consultando");

    try {
      const res = await mercantilBuscarYRegistrar(ident);
      const data = res?.data?.data;

      // Transición visual de pasos para que el usuario perciba progreso
      setPaso("validando");
      await new Promise((r) => setTimeout(r, 300));
      setPaso("registrando");
      await new Promise((r) => setTimeout(r, 300));
      setPaso("done");

      setResultado(data);

      if (data?.estatus === "creada") {
        toast.success(`Orden ${ident} registrada exitosamente en Farme.`);
        if (onSuccess) onSuccess(ident);
        buscarSolicitudId(ident);
      } else if (data?.estatus === "omitida") {
        toast.info(`Orden ${ident} ya existe en Farme.`);
        buscarSolicitudId(ident);
      } else {
        toast.error(
          `No se pudo registrar ${ident}: ${data?.error_detalle || "Error desconocido"}`
        );
      }
    } catch (error) {
      console.error("Error en búsqueda Mercantil:", error);
      const msg = mensajeErrorAmigable(error);
      toast.error(msg);
      setResultado({ estatus: "fallida", error_detalle: msg, identificador: ident });
      setPaso("done");
    } finally {
      setBuscando(false);
    }
  };

  const abrirSolicitud = () => {
    if (solicitudIdEncontrada) {
      window.open(`/solicitud/ver/${solicitudIdEncontrada}`, "_blank", "noopener");
    }
  };

  const isError = resultado && resultado.estatus !== "creada" && resultado.estatus !== "omitida";

  return (
    <Modal isOpen={isOpen} toggle={buscando ? undefined : toggle} size="lg" backdrop={buscando ? "static" : true}>
      <ModalHeader toggle={buscando ? undefined : toggle}>
        <i className="ri-search-line me-2"></i>
        Buscar y Registrar Orden Mercantil
      </ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <Label className="form-label">Identificador de Orden Mercantil</Label>
          <Input
            type="text"
            placeholder="Ej: A0000711904"
            value={identificador}
            onChange={(e) => setIdentificador(e.target.value.toUpperCase())}
            disabled={buscando}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !buscando) handleBuscar();
            }}
            autoFocus
          />
          <small className="text-muted d-block mt-1">
            Ingrese el número de orden exacto del portal Mercantil.
            <strong className="ms-1">La consulta puede tardar entre 60 y 90 segundos</strong>;
            no cierre esta ventana mientras se procesa.
          </small>
        </div>

        {(buscando || paso === "done") && (
          <StepsIndicator paso={paso} error={isError} />
        )}

        {resultado && (
          <Alert
            color={
              resultado.estatus === "creada"
                ? "success"
                : resultado.estatus === "omitida"
                ? "info"
                : "danger"
            }
            className="mb-0"
          >
            <div className="d-flex align-items-start">
              <i
                className={`fs-22 me-2 ${
                  resultado.estatus === "creada"
                    ? "ri-checkbox-circle-line"
                    : resultado.estatus === "omitida"
                    ? "ri-information-line"
                    : "ri-close-circle-line"
                }`}
              ></i>
              <div className="flex-grow-1">
                <strong>
                  {resultado.estatus === "creada"
                    ? "Orden registrada"
                    : resultado.estatus === "omitida"
                    ? "Orden ya existente"
                    : "No se pudo registrar"}
                </strong>
                <p className="mb-1 fs-13">
                  {resultado.estatus === "creada"
                    ? `La orden ${resultado.identificador} fue creada exitosamente en Farme.`
                    : resultado.estatus === "omitida"
                    ? `La orden ${resultado.identificador} ya existe en el sistema.`
                    : resultado.error_detalle || "Ocurrió un error inesperado."}
                </p>
                {resultado.duracion_ms > 0 && (
                  <small className="text-muted">
                    Duración: {(resultado.duracion_ms / 1000).toFixed(1)}s
                  </small>
                )}
                {(resultado.estatus === "creada" || resultado.estatus === "omitida") && (
                  <div className="mt-2">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={abrirSolicitud}
                      disabled={buscandoEnlace || !solicitudIdEncontrada}
                    >
                      {buscandoEnlace ? (
                        <>
                          <LoaderButton />
                          Localizando solicitud...
                        </>
                      ) : (
                        <>
                          <i className="ri-external-link-line me-1"></i>
                          Abrir solicitud en nueva pestaña
                        </>
                      )}
                    </Button>
                    {!buscandoEnlace && !solicitudIdEncontrada && (
                      <small className="text-muted d-block mt-1">
                        No se pudo localizar el enlace directo. La solicitud aparece en el listado
                        (se refrescó automáticamente).
                      </small>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={buscando}>
          Cerrar
        </Button>
        <Button
          color="primary"
          onClick={handleBuscar}
          disabled={buscando || !identificador.trim()}
        >
          {buscando ? (
            <>
              <LoaderButton />
              Procesando...
            </>
          ) : (
            <>
              <i className="ri-search-line me-1"></i>
              {resultado ? "Buscar otra" : "Buscar y Registrar"}
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default MercantilBuscarRegistrarModal;
